import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Combobox from '../components/Combobox';
import DrawerBody from './DrawerBody';
import RecordReceiptPanel from '../pages/Procurement/receipt/RecordReceiptPanel';
import './createForms.css';
import { Emo, emoText } from './EmojiIcons';
import DrawerHeader from './drawer/DrawerHeader';

// 45138 — Record Receipt, the global side drawer (registered as form 'receipt' in CreateDrawerHost). Head (eyebrow, expand/close, title, order state rail) +
// ORDER FIELD + the shared RecordReceiptPanel. Context (params from the opener, see Prompts/0.45/45138_receipt_drawer_CONTRACT.md):
//   operationId        -> that order, LOCKED (the purchase order page); scopeLineId narrows it to one line
//   partner_id (+name) -> picker over that supplier's receivable orders, supplier shown locked (the supplier page)
//   neither            -> free picker over every receivable order (+ Create / Purchasing + Data Entry)
// The picker is a server-side searchable combobox over GET /goods-receipts/receivable-orders: only posted orders with a line en_route|partial are offered.
const RAIL = ['en_route', 'partial', 'received'];

function OrderField({ params, op, setOp }) {
  const { t } = useTranslation('receipt');
  const locked = !!params?.operationId, supplierLocked = !locked && !!params?.partner_id;
  const [orders, setOrders] = useState(null), [err, setErr] = useState('');
  const known = useRef(new Map()), timer = useRef(null);
  const search = q => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setOrders(null); setErr(''); api.getReceivableOrders(q.trim(), supplierLocked ? params.partner_id : '').then(r => setOrders(r.orders || [])).catch(e => { setOrders([]); setErr(e.message || t('picker.loadErr')); }); }, 250);
  };
  useEffect(() => { if (!locked) search(''); return () => clearTimeout(timer.current); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  if (locked) return (
    <div className="field"><label>{t('field.order')} · {t('locked')}</label><div className="rcp-pinned" data-testid="rcp-order-locked">{params.operationId}</div></div>);
  (orders || []).forEach(o => known.current.set(o.operation_id, o));
  const label = o => `${o.operation_id} · ${o.partner_name || '—'} · ${Number(o.line_count) > 1 ? t('picker.itemsChip', { count: o.line_count }) : (o.item_name || '—')}`;
  const options = (orders || []).map(o => ({ value: o.operation_id, label: label(o), search: `${label(o)} ${(o.item_names || []).join(' ')}` }));
  if (op && known.current.has(op) && !options.some(x => x.value === op)) { const o = known.current.get(op); options.unshift({ value: op, label: label(o), search: label(o) }); }
  const supplierName = params?.partner_name || (orders || [])[0]?.partner_name || known.current.get(op)?.partner_name || '';
  return (
    <>
      {supplierLocked && <div className="field"><label>{t('field.supplier')} · {t('locked')}</label><div className="rcp-pinned" data-testid="rcp-supplier-locked">{supplierName || params.partner_id}</div></div>}
      <div className="field"><label htmlFor="rcp-order">{t('field.order')}</label>
        <div data-testid="rcp-order"><Combobox id="rcp-order" options={options} value={op} onChange={setOp} onQueryChange={search}
          placeholder={orders === null ? t('loading') : t('picker.placeholder')} style={{ display: 'block', width: '100%' }} /></div>
        <span className="sub">{t('picker.hint')}</span></div>
      {err && <div className="errbox" role="alert" data-testid="rcp-list-error">{err}</div>}
      {orders && orders.length === 0 && !op && !err && <div className="hint" data-testid="rcp-none">{t(supplierLocked ? 'picker.emptySupplier' : 'picker.empty')} {t('picker.emptyHint')}</div>}
    </>
  );
}

export default function ReceiptCreateForm({ size, onSize, onClose, onInteract, params }) {
  const { t } = useTranslation('receipt');
  const [op, setOp] = useState(params?.operationId || '');
  const [rail, setRail] = useState('en_route');
  const meta = op ? op : t('subtitle');
  const posted = () => window.dispatchEvent(new CustomEvent('purchase-receipt-posted', { detail: { operationId: op } }));
  return (
    <>
      <DrawerHeader eyebrow={t('eyebrow')} ini={<Emo name="delivery" />} title={<>{emoText(t('title'))}</>} size={size} onSize={onSize} onClose={onClose}>
        <div className="dr-meta" data-testid="rcp-subtitle">{meta}</div>
        {op && <div className="rail" data-testid="rcp-rail">{RAIL.map((k, i) => <React.Fragment key={k}>{i > 0 && <span className="arh">→</span>}<span className={'st' + (k === rail ? ' now' : i < RAIL.indexOf(rail) ? ' done' : '')}>{t('rail.' + k)}</span></React.Fragment>)}</div>}
      </DrawerHeader>
      <DrawerBody onClose={onClose}>
        <OrderField params={params} op={op} setOp={v => { setOp(v || ''); onInteract && onInteract(false); }} />
        {op && <RecordReceiptPanel key={op + (params?.scopeLineId || '')} operationId={op} scopeLineId={params?.operationId === op ? params?.scopeLineId || null : null}
          onRail={setRail} onDirty={d => onInteract && onInteract(d, op)} onPosted={posted} onClose={onClose} />}
      </DrawerBody>
    </>
  );
}
